import React, { FC, useState } from "react";

import styled from "@emotion/styled";
import { Link, navigate } from "gatsby";

import * as yup from "yup";
import { useFormik } from "formik";

import PropTypes from "prop-types";
import classnames from "classnames";

import Input from "../Input";
import Textarea from "../Textarea";
import Turnstile from "../Turnstile";
import { FormStatus } from "../ApplicationForm";

import { sendEmailWithValues } from "../../utils/send-email";

const TEMPLATE_ID = "mosano_contact";

interface ContactFormValues {
  name: string;
  email: string;
  phone: string;
  organization: string;
  about: string;
  captchaToken: string;
}

interface iProps {
  TEMPLATE_ID?: string;
  type: "service" | "contact";
}

const ContactForm: FC<iProps> = (props) => {
  const [formStatus, setFormStatus] = useState<FormStatus>(FormStatus.Idle);

  const validationSchema = yup.object({
    name: yup.string().required("Name is required"),
    about:
      props.type === "service"
        ? yup.string().required("About is required")
        : yup.string(),
    phone:
      props.type === "service"
        ? yup.string().required("Phone is required")
        : yup.string(),
    captchaToken: yup.string().required("Human verification is required"),
    email: yup
      .string()
      .email("Value must be a valid email")
      .required("Email is required"),
  });

  const disabled =
    formStatus === FormStatus.Sending || formStatus === FormStatus.Sent;
  const formik = useFormik<ContactFormValues>({
    validateOnBlur: false,
    validateOnMount: false,
    validateOnChange: false,
    validationSchema,
    initialValues: {
      name: "",
      email: "",
      organization: "",
      about: "",
      phone: "",
      captchaToken: "",
    },
    onSubmit: async (values) => {
      if (disabled) return;
      setFormStatus(FormStatus.Sending);
      try {
        const finalValues = {
          ...values,
          "cf-turnstile-response": values.captchaToken,
        };
        delete finalValues.captchaToken;

        await sendEmailWithValues(
          finalValues,
          props.TEMPLATE_ID || TEMPLATE_ID
        );
        setFormStatus(FormStatus.Sent);
        navigate("/thanks?service-page");
      } catch (err) {
        setFormStatus(FormStatus.Errored);
      }
    },
  });

  return (
    <div
      className={`${
        props.type === "service"
          ? "max-width-application-form container-form service__contact-form-container"
          : "contact-container-form contact__contact-form-container"
      }`}
    >
      <h1
        className={`heading ${
          props.type === "contact" ? "heading-margin" : ""
        }`}
      >
        How can we help?
      </h1>
      <p className="p1 full"></p>
      <div className="div-block-62"></div>
      <div className="form-block w-form">
        <form
          method="POST"
          id="contact-form"
          name="contact-form"
          encType="multipart/form-data"
          onSubmit={formik.handleSubmit}
        >
          <Input
            label="Name*"
            id="name"
            type="text"
            name="name"
            data-name="Name"
            className="text-field w-input"
            maxLength={128}
            disabled={disabled}
            value={formik.values.name}
            error={formik.errors.name}
            onChange={formik.handleChange}
          />
          <ContactsRow>
            <div style={{ flex: 1 }}>
              <Input
                label="Email*"
                id="email"
                type="email"
                name="email"
                data-name="Email"
                className="text-field w-input"
                maxLength={128}
                disabled={disabled}
                value={formik.values.email}
                error={formik.errors.email}
                onChange={formik.handleChange}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Input
                label={props.type === "service" ? "Phone*" : "Phone"}
                id="phone"
                type="tel"
                name="phone"
                data-name="phone"
                className="text-field w-input"
                maxLength={64}
                disabled={disabled}
                value={formik.values.phone}
                error={formik.errors.phone}
                onChange={formik.handleChange}
              />
            </div>
          </ContactsRow>
          {props.type === "service" ? (
            <ContactsRow>
              <div style={{ flex: 1 }}>
                <Input
                  label="Organization or Company*"
                  id="organization"
                  name="organization"
                  data-name="organization"
                  className="text-field w-input"
                  maxLength={128}
                  disabled={disabled}
                  value={formik.values.organization}
                  error={formik.errors.organization}
                  onChange={formik.handleChange}
                />
              </div>
            </ContactsRow>
          ) : null}
          <Textarea
            label={
              props.type === "service"
                ? "Tell us a bit about your project and what do you need*"
                : "Tell us a bit about your project and what do you need"
            }
            id="about"
            name="about"
            data-name="about"
            className="text-field-copy w-input"
            maxLength={512}
            disabled={disabled}
            value={formik.values.about}
            error={formik.errors.about}
            onChange={formik.handleChange}
          />
          <CaptchaRow>
            <Turnstile
              error={formik.errors.captchaToken}
              sitekey={process.env.GATSBY_TURNSTILE_SITE_KEY}
              onChange={(token) => formik.setFieldValue("captchaToken", token)}
            />
          </CaptchaRow>
          <div className="div-block-64">
            <div className="p3">
              The information provided will be processed according to our{" "}
              <Link to="/privacypolicy">Privacy Policy</Link>.
            </div>
            <div className="div-block-66"></div>
            <span className={classnames("mosano-button-outer", { disabled })}>
              <div className="button-container-details">
                <button
                  type="submit"
                  data-wait="Please wait..."
                  className="button-details hover-button w-button"
                  style={{ width: "auto", whiteSpace: "nowrap" }}
                  disabled={disabled}
                >
                  send message
                </button>
              </div>
            </span>
          </div>
        </form>
        <div
          className="w-form-done"
          style={{ display: formStatus === FormStatus.Sent && "block" }}
        >
          <div>Thank you! Your contact has been received!</div>
        </div>
        <div
          className="w-form-fail"
          style={{ display: formStatus === FormStatus.Errored && "block" }}
        >
          <div>
            Oops! Something went wrong while submitting the form. Please try
            again.
          </div>
        </div>
      </div>
    </div>
  );
};

const ContactsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  margin: 3rem 0;
  gap: 3rem;
`;

const CaptchaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  margin: 3rem 0;
`;

export default ContactForm;

ContactForm.propTypes = {
  containerType: PropTypes.string,
  applicationType: PropTypes.string,
  jobTitle: PropTypes.string,
  jobInfo: PropTypes.string,
};
